/** Colour parsing and interpolation in sRGB. Accepts #RGB, #RGBA, #RRGGBB, #RRGGBBAA. */
export type Rgba = [number, number, number, number];

export function parseColor(s: string): Rgba | undefined {
  if (!/^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) return undefined;
  let h = s.slice(1);
  if (h.length <= 4) h = [...h].map((c) => c + c).join('');
  const n = (i: number) => parseInt(h.slice(i, i + 2), 16);
  return [n(0), n(2), n(4), h.length === 8 ? n(6) / 255 : 1];
}

export function isColor(s: unknown): s is string {
  return typeof s === 'string' && parseColor(s) !== undefined;
}

/** Channels are clamped (0..255, alpha 0..1): an overshooting easing (back, bezier) extrapolates past both ends. */
export function formatColor(c: Rgba): string {
  const ch = (v: number, max: number) => (v > 0 ? Math.min(max, v) : 0);
  const r = Math.round(ch(c[0], 255));
  const g = Math.round(ch(c[1], 255));
  const b = Math.round(ch(c[2], 255));
  const a = Math.round(ch(c[3], 1) * 1000) / 1000;
  return a >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`;
}

export function mixColor(a: string, b: string, p: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return p < 1 ? a : b;
  return formatColor([0, 1, 2, 3].map((i) => ca[i]! + (cb[i]! - ca[i]!) * p) as Rgba);
}

// OKLab (Björn Ottosson): blends that keep perceived lightness and hue, for colour tracks with colorSpace 'oklab'.
const toLinear = (c: number) => ((c /= 255) <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toSrgb = (c: number) => 255 * (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function toOklab(c: Rgba): Rgba {
  const [r, g, b] = [toLinear(c[0]), toLinear(c[1]), toLinear(c[2])];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s, c[3]];
}

export function fromOklab(c: Rgba): Rgba {
  const l = (c[0] + 0.3963377774 * c[1] + 0.2158037573 * c[2]) ** 3;
  const m = (c[0] - 0.1055613458 * c[1] - 0.0638541728 * c[2]) ** 3;
  const s = (c[0] - 0.0894841775 * c[1] - 1.291485548 * c[2]) ** 3;
  const k = (v: number) => Math.min(255, Math.max(0, toSrgb(v)));
  return [k(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s), k(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s), k(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s), c[3]];
}

export function mixOklab(a: string, b: string, p: number): string {
  const ca = parseColor(a);
  const cb = parseColor(b);
  if (!ca || !cb) return p < 1 ? a : b;
  const la = toOklab(ca);
  const lb = toOklab(cb);
  return formatColor(fromOklab([0, 1, 2, 3].map((i) => la[i]! + (lb[i]! - la[i]!) * p) as Rgba));
}
